import React, {useState, useMemo} from 'react';
import {Box, Divider, Stack, styled} from '@mui/material';
import {
  Body,
  SwitchButton,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import type {Avatar, Voice} from '@contentlab/api/avatars';
import AvatarSelector from '@contentlab/components/avatar-selector';
import type {DraftAiAvatarProject} from '@contentlab/stores/contentLabStore';
import {AvatarKind} from '@contentlab/types/aiAvatars';
import type {AvatarFilter} from '@contentlab/types/avatarFilters';
import ConfigButton from '@contentlab/views/contentlab/pages/ai-avatar-page/components/config-button';
import translate from '@/utils/translate';
import AvatarFiltersBar from '../../avatar-filters-bar';
import CustomAvatarEditor from './CustomAvatarEditor';
import VoiceOnlyEditor from './VoiceOnlyEditor';

enum Tabs {
  READY = 'ready',
  CUSTOM = 'custom',
}

interface AvatarEditorProps {
  testID?: string;
  onAvatarSelect?: (avatar: Avatar) => void;
  selectedAvatar?: Avatar | null;
  avatarSpec: DraftAiAvatarProject['avatarSpec'];
  selectedAvatarKind: AvatarKind | null;
  onSelectKind?: (kind: AvatarKind) => void;
  onOpenConfig?: () => void;
  voiceOnlySelectedId?: string | null;
  customSelectedVoiceId?: string | null;
  onVoiceOnlySelectVoice?: (voice: Voice) => void;
  onCustomSelectVoice?: (voice: Voice) => void;
  onCustomSelectImage?: (file: File) => void | Promise<void>;
  isAvatarPreviewMuted?: boolean;
  onAvatarPreviewMuteChange?: (avatar: Avatar, muted: boolean) => void;
  avatarFilters?: AvatarFilter[];
  onAvatarFilterChange?: (id: string, selectedOptionId: string[]) => void;
  avatars: Avatar[];
  voices: Voice[];
  isLoadingAvatarsOrVoices?: boolean;
}

const TabsRow = styled(Box)(() => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  paddingInlineEnd: '24px',
}));

const TabsContainer = styled(Box)(() => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  borderBottom: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
}));

const shouldNotForwardProps = ['isActive', 'isComingSoon'];

const Tab = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{
  isActive?: boolean;
  isComingSoon?: boolean;
}>(({isActive, isComingSoon}) => ({
  display: 'flex',
  padding: '16px 8px',
  gap: '8px',
  color: (() => {
    if (isActive) {
      return designSystemToken('semantic.fg.primary');
    }
    if (isComingSoon) {
      return designSystemToken('semantic.fg.disabled');
    }
    return designSystemToken('semantic.fg.secondary');
  })(),
  cursor: isComingSoon ? 'default' : 'pointer',
  borderBottom: isActive
    ? `2px solid ${designSystemToken('semantic.fg.primary')}`
    : '2px solid transparent',
}));

const SwitchButtonContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '9px',
  cursor: 'pointer',
}));

function AvatarEditor({
  testID = 'avatar-editor',
  onAvatarSelect,
  selectedAvatar,
  avatarSpec,
  selectedAvatarKind,
  onSelectKind,
  onOpenConfig,
  voiceOnlySelectedId,
  customSelectedVoiceId,
  onVoiceOnlySelectVoice,
  onCustomSelectVoice,
  onCustomSelectImage,
  isAvatarPreviewMuted,
  onAvatarPreviewMuteChange,
  avatarFilters,
  onAvatarFilterChange,
  avatars,
  voices,
  isLoadingAvatarsOrVoices,
}: AvatarEditorProps) {
  const initialTab: Tabs =
    selectedAvatarKind === AvatarKind.CUSTOM ? Tabs.CUSTOM : Tabs.READY;
  const [activeTab, setActiveTab] = useState<Tabs>(initialTab);
  const isVoiceOnly = selectedAvatarKind === AvatarKind.VOICE_ONLY;

  const selectType = (type: AvatarKind) => onSelectKind?.(type);

  const TAB_ITEMS: Array<{label: string; value: Tabs; isComingSoon?: boolean}> =
    [
      {label: translate('Library'), value: Tabs.READY},
      {label: translate('Create'), value: Tabs.CUSTOM, isComingSoon: true},
    ];

  const onTabChange = (next: Tabs) => {
    setActiveTab(next);
    if (next === Tabs.READY) {
      const hasVoiceOnly = !!(
        avatarSpec.voiceIdVoiceOnly || avatarSpec.voiceId
      );
      const hasLibrary = !!avatarSpec.libraryId;
      if (isVoiceOnly && hasVoiceOnly) {
        selectType(AvatarKind.VOICE_ONLY);
      } else if (!isVoiceOnly && hasLibrary) {
        selectType(AvatarKind.LIBRARY);
      }
    } else if (next === Tabs.CUSTOM) {
      // Mark mode explicitly to avoid voice-only sticking
      selectType(AvatarKind.CUSTOM);
    }
  };

  const configDisabled = useMemo(() => {
    if (selectedAvatarKind === AvatarKind.VOICE_ONLY) {
      return true;
    }
    if (selectedAvatarKind === AvatarKind.LIBRARY) {
      return !avatarSpec.libraryId;
    }
    if (selectedAvatarKind === AvatarKind.CUSTOM) {
      return !avatarSpec.imageAssetId;
    }
    return true;
  }, [selectedAvatarKind, avatarSpec.libraryId, avatarSpec.imageAssetId]);

  const renderActiveTab = () => {
    if (activeTab === Tabs.CUSTOM) {
      return (
        <CustomAvatarEditor
          selectedId={customSelectedVoiceId ?? null}
          onSelectVoice={(voice) => onCustomSelectVoice?.(voice)}
          onSelectImage={(file) => onCustomSelectImage?.(file)}
        />
      );
    }
    if (isVoiceOnly) {
      return (
        <VoiceOnlyEditor
          selectedId={voiceOnlySelectedId ?? null}
          onSelectVoice={(voice) => onVoiceOnlySelectVoice?.(voice)}
          voices={voices}
          isLoading={isLoadingAvatarsOrVoices}
        />
      );
    }
    return (
      <AvatarSelector
        onAvatarSelect={onAvatarSelect}
        selectedAvatar={selectedAvatar ?? null}
        isAvatarPreviewMuted={isAvatarPreviewMuted}
        onAvatarPreviewMuteChange={onAvatarPreviewMuteChange}
        avatars={avatars}
        isLoading={isLoadingAvatarsOrVoices}
      />
    );
  };

  return (
    <Box sx={{height: '100%', boxSizing: 'border-box'}} data-testid={testID}>
      <Stack sx={{height: '100%', boxSizing: 'border-box'}} gap="16px">
        <TabsRow>
          <TabsContainer>
            {TAB_ITEMS.map((tabItem) => (
              <Tab
                key={tabItem.value}
                isActive={activeTab === tabItem.value}
                onClick={
                  !tabItem.isComingSoon
                    ? () => onTabChange(tabItem.value)
                    : undefined
                }
                isComingSoon={!!tabItem.isComingSoon}
              >
                <Label size="md" color="inherit">
                  {tabItem.label}
                </Label>
                {tabItem.isComingSoon ? (
                  <Box
                    sx={{
                      display: 'flex',
                      padding: '0 4px',
                      alignItems: 'center',
                      backgroundColor: designSystemToken(
                        'semantic.bg.brand-secondary'
                      ),
                      borderRadius: '4px',
                    }}
                  >
                    <Label
                      size="xxs"
                      color={designSystemToken('semantic.fg.secondary')}
                    >
                      {translate('Coming soon!')}
                    </Label>
                  </Box>
                ) : null}
              </Tab>
            ))}
          </TabsContainer>
          <Box sx={{flex: 1}} />
          <ConfigButton
            disabled={configDisabled}
            onClick={onOpenConfig}
            hideIcon
          >
            {translate('Adjust Layout')}
          </ConfigButton>
        </TabsRow>

        <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          {activeTab === Tabs.READY && (
            <>
              <SwitchButtonContainer
                onClick={() => {
                  if (isVoiceOnly) {
                    selectType(AvatarKind.LIBRARY);
                  } else {
                    selectType(AvatarKind.VOICE_ONLY);
                  }
                }}
              >
                <SwitchButton
                  checked={isVoiceOnly}
                  checkedTrackColor={designSystemToken('semantic.bg.neutral')}
                />
                <Body size="md" sx={{textWrap: 'nowrap'}}>
                  {translate('Voice only')}
                </Body>
              </SwitchButtonContainer>
              <Divider
                orientation="vertical"
                sx={{
                  backgroundColor: designSystemToken('semantic.bg.tertiary'),
                  height: '24px',
                }}
              />
              {/* Avatar filters bar */}
              {avatarFilters && onAvatarFilterChange && (
                <AvatarFiltersBar
                  filters={avatarFilters}
                  onFilterChange={onAvatarFilterChange}
                />
              )}
            </>
          )}
        </Box>

        {/* Content */}
        {renderActiveTab()}
      </Stack>
    </Box>
  );
}

export default AvatarEditor;
